<template>
  <div class="menu-list-entry" :class="{ 'gold-border-box': hasPrio }">
    <b-row
      class="announcement-entry mx-auto w-100 p-3"
      :class="{ expanded: expanded }"
      align-h="center"
    >
      <b-col cols="3" class="d-flex flex-column align-items-center">
        <img
          draggable="false"
          class="news-icon"
          :class="{ clickable: customLink }"
          :src="newsEntryImg"
          @click="openCustomLink"
        />
      </b-col>

      <b-col class="ps-0 pt-2 d-flex flex-column justify-content-center">
        <div class="news-text">
          <div class="headline d-inline-flex align-items-center">
            <div v-if="isUnread" class="new-message-icon"></div>

            <span
              :class="{ highlight: hasPrio }"
              v-html="$t(entry.title)"
            ></span>
            <span class="small text-right ms-5">{{ entryTimestamp }}</span>
          </div>
          <div class="subtext">
            <span v-html="newsEntryText"> </span>
            <button
              v-if="(entry.markdown_content || '').length > maxCharacters"
              class="button-blue-outline ms-3"
              style="font-size: 12px; padding: 4px"
              @click="handleExpand"
            >
              {{ expandButtonText }}
            </button>
            <!-- entry images -->
            <template v-if="hasGallery && expanded">
              <div
                class="w-100 d-inline-flex align-items-center news-gallery mt-3 p-3"
              >
                <img
                  v-for="(image, index) in entry.gallery"
                  :key="'gallery-image' + index"
                  :alt="image.name"
                  class="news-gallery-image me-2 clickable"
                  :src="image.url"
                  @click="setLargeImage(image.url)"
                />
              </div>
              <b-modal
                v-model="showLargeImage"
                hide-header
                hide-footer
                lazy
                modal-class="image-modal"
                dialog-class="image-modal-dialog"
                content-class="image-modal-content"
                body-class="image-modal-body"
              >
                <img :src="largeImgUrl" class="image-modal-img" />
              </b-modal>
            </template>
            <!-- related links section -->
            <template
              v-if="
                expanded && entry.links && Object.keys(entry.links).length > 0
              "
            >
              <div class="related-links mt-4 p-3">
                <h4
                  class="related-title text-white mb-3 d-flex align-items-center"
                >
                  <img
                    src="@/assets/img/icons/link.png"
                    alt="link icon"
                    class="related-icon me-2"
                  />
                  <span>Related Links</span>
                </h4>

                <div class="link-card-grid">
                  <div
                    v-for="(title, url, index) in entry.links"
                    :key="'related-link-' + index"
                    class="link-card"
                    @click="openLink(url)"
                  >
                    <div class="icon-circle me-3">
                      <img
                        :src="getFavicon(url)"
                        alt="favicon"
                        class="favicon"
                        @error="onFaviconError"
                      />
                    </div>
                    <div class="text">
                      <div class="title">{{ title }}</div>
                      <div class="url">{{ getHostname(url) }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </template>
            <!-- in case reward items are associated with the entry -->
            <div
              v-if="entry.payload && entry.payload.items"
              class="d-inline-flex align-items-center"
            >
              <RewardItem
                v-for="reward in entry.payload.items"
                :key="reward.item.id"
                :item="reward.item"
                class="me-3"
              ></RewardItem>
            </div>
            <!-- social media icons and links -->
            <template v-if="socialMediaLinks.length > 0">
              <div class="d-inline-flex align-items-center mt-2">
                <span class="uppercase text-white me-3"
                  >{{ $t("events.button_more_info") }}:</span
                >
                <a
                  v-for="(link, index) in socialMediaLinks"
                  :key="'social-link-' + index"
                  :href="link.url"
                  target="_blank"
                  class="social-icon me-2 clickable"
                  :class="link.title.toLowerCase()"
                ></a></div
            ></template>
          </div>
        </div>
      </b-col>
    </b-row>
  </div>
</template>
<script>
import { marked } from "marked";
export default {
  name: "AnnouncementEntry",

  props: {
    entry: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      maxCharacters: 120,
      expanded: false,
      showLargeImage: false,
      largeImgUrl: "",
      socialMedia: ["Facebook", "Instagram", "WhatsApp"],
    };
  },

  computed: {
    customLink() {
      return (
        Object.values(this.entry.links || {}).find(
          (link) => !this.socialMedia.includes(link.title)
        )?.url || null
      );
    },
    socialMediaLinks() {
      return Object.entries(this.entry.links || {})
        .filter(([_, title]) => this.socialMedia.includes(title))
        .map(([url, title]) => ({ title, url }));
    },
    hasGallery() {
      return (
        this.entry.gallery &&
        Array.isArray(this.entry.gallery) &&
        this.entry.gallery.length > 0
      );
    },
    newsEntryText() {
      let text = this.entry.markdown_content || "";

      // Trim for collapsed view
      if (text.length > this.maxCharacters && !this.expanded) {
        text = text.slice(0, this.maxCharacters).trim() + "…";
      }

      // Convert markdown → HTML
      const html = marked(text, { breaks: true });

      return html;
    },
    newsEntryImg() {
      if (this.entry.banner && this.entry.banner.length > 0) {
        return this.entry.banner[0].url;
      } else return require("@/assets/img/notification/icn-nc-lobby.png");
    },
    entryTimestamp() {
      return this.entry.published_at
        ? new Date(this.entry.published_at).toLocaleString(
            this.$store.state.locale
          )
        : "";
    },
    expandButtonText() {
      return this.expanded ? "<<" : ">>";
    },
    hasPrio() {
      return this.entry.priority > 0;
    },
    isUnread() {
      return this.$store.getters["news/unreadMessages"].some((msg) => {
        return msg.id === this.entry.id;
      });
    },
  },
  mounted() {
    if (this.hasPrio) {
      this.expanded = true;
      this.$store.commit("news/markAsRead", this.entry.id);
    }
  },
  methods: {
    openCustomLink() {
      if (!this.customLink) return;
      let linksArr = Object.keys(this.entry.links);
      window.open(linksArr[0], "_blank");
    },
    handleExpand() {
      this.expanded = !this.expanded;
      if (this.expanded) {
        this.$store.commit("news/markAsRead", this.entry.id);
        this.firebaseClickItemAnnouncement(this.entry.id);
      }
    },
    setLargeImage(url) {
      this.largeImgUrl = url;
      this.showLargeImage = true;
    },
    openLink(url) {
      window.open(url, "_blank");
    },
    getFavicon(url) {
      try {
        const { hostname } = new URL(url);
        return require("@/assets/img/common/default-fav.png");
      } catch (e) {
        return require("@/assets/img/common/default-fav.png");
      }
    },
    onFaviconError(event) {
      event.target.src = require("@/assets/img/common/default-fav.png");
    },
    getHostname(url) {
      try {
        return new window.URL(url).hostname;
      } catch (e) {
        return url;
      }
    },
  },
};
</script>
<style lang="scss">
.menu-list-entry {
  position: relative;
  &.gold-border-box {
    position: sticky;
    top: 0;
    z-index: 1000;
  }

  .news-icon {
    max-width: 200px;
    max-height: 200px;
    border-radius: 5px;
  }

  .news-text {
    text-align: left;

    .headline {
      font-size: 1.8rem;
      font-family: Ubuntu-Bold;
      line-height: 1;
      margin-bottom: 10px;
      .highlight {
        color: $text-highlight-yellow;
      }
    }

    .subtext {
      margin-top: 10px;
      font-size: 1.5rem;
      font-family: Ubuntu;
      line-height: 1;
      padding-right: 20px;
    }
  }
  .news-text {
    .subtext {
      margin-top: 10px;
      font-size: 1.7rem;
      font-family: Ubuntu;
      line-height: 1.6;
      padding-right: 20px;
      color: #fff;

      p {
        margin-bottom: 0.8em;
      }

      ul,
      ol {
        margin: 0.8em 0 0.8em 1.5em;
      }

      a {
        color: #4da3ff;
        text-decoration: underline;
      }

      strong {
        font-weight: bold;
      }
    }
  }

  .social-icon {
    display: inline-block;
    width: 32px;
    height: 32px;
    background-size: cover;
    margin-right: 8px;

    &.facebook {
      background-image: url(~@/assets/img/common/facebook.png);
    }
    &.instagram {
      background-image: url(~@/assets/img/common/instagram.png);
    }
    &.whatsapp {
      background-image: url(~@/assets/img/common/whatsapp.png);
    }
  }
  .news-gallery {
    overflow-x: auto;
    overflow-y: hidden;
    white-space: nowrap;
    padding: 10px 0;

    .news-gallery-image {
      max-height: 100px;
      max-width: 100px;
      margin-right: 10px;
      border-radius: 5px;
      object-fit: cover;
    }
    .large-image {
      max-height: 80vh;
      max-width: 80vw;
      object-fit: contain;
    }
  }
  .related-links {
    background: rgba(255, 255, 255, 0.06);
    border-radius: 10px;
    padding: 15px 20px;

    h4 {
      font-family: Ubuntu-Bold;
      color: $text-highlight-yellow;
      font-size: 1.6rem;
    }
    .related-title {
      display: flex;
      align-items: center;
      gap: 8px;

      .related-icon {
        width: 22px;
        height: 22px;
        object-fit: contain;
      }
    }

    .link-card-grid {
      display: flex;
      flex-wrap: wrap;
      gap: 12px;
    }

    .link-card {
      flex: 1 1 45%;
      min-width: 220px;
      display: flex;
      align-items: center;
      background: rgba(255, 255, 255, 0.08);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 8px;
      padding: 10px 15px;
      cursor: pointer;
      transition: all 0.2s ease-in-out;

      &:hover {
        transform: translateY(-2px);
        background: rgba(255, 255, 255, 0.12);
      }

      .icon-circle {
        background: #4da3ff33;
        border-radius: 50%;
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;

        .favicon {
          width: 24px;
          height: 24px;
        }
      }

      .text {
        .title {
          color: #fff;
          font-weight: bold;
          font-size: 1rem;
        }
        .url {
          color: #aaa;
          font-size: 0.8rem;
        }
      }
    }
  }
}
.image-modal-dialog {
  margin: 0;
  max-width: none;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-modal-content {
  background: transparent !important;
  border: 0 !important;
  box-shadow: none !important;
  width: auto;
  height: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.image-modal-body {
  padding: 0 !important;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.image-modal-img {
  width: auto;
  height: auto;
  max-width: 80vw;
  max-height: 80vh;
  object-fit: contain;
  display: block;
  pointer-events: auto;
  border-radius: 8px;
  transition: transform 0.2s ease-in-out;
}
</style>
